import React from "react";
import SEO from "../components/SEO";
import styled from "styled-components";

const PolitiqueContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  color: #b0b0b0; /* Softer light grey for main text */

  h1 {
    color: #13519a; /* Consistent blue for titles */
    margin-bottom: 1.5rem;
    text-align: center;
  }

  h2 {
    color: #13519a; /* Consistent blue for titles */
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  p,
  ul {
    margin-bottom: 1rem;
  }

  ul {
    list-style-type: disc;
    margin-left: 20px;
  }

  a {
    color: #ef6500; /* Consistent link color */
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Bouton au style d'un lien, pour rester dans le fil du texte
const ConsentButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--color-accent);
  text-decoration: underline;
  cursor: pointer;
`;

// Efface le choix enregistré et les cookies Google Analytics (_ga, _ga_XXXX),
// puis recharge : le bandeau cookies réapparaît
const resetConsent = () => {
  try {
    localStorage.removeItem("gdpr_cookie_consent");
  } catch (e) {
    // stockage indisponible : rien à effacer
  }
  const domaine = window.location.hostname.replace(/^www\./, "");
  document.cookie.split(";").forEach((c) => {
    const nom = c.split("=")[0].trim();
    if (nom.startsWith("_ga")) {
      const expire = `${nom}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = expire;
      document.cookie = `${expire}; domain=.${domaine}`;
    }
  });
  window.location.reload();
};

const PolitiqueConfidentialite = () => {
  return (
    <PolitiqueContainer>
      <SEO
        title="Politique de Confidentialité | ImpactIsol - RGPD"
        description="Politique de confidentialité et protection des données personnelles d'ImpactIsol. Conformité RGPD et droits des utilisateurs."
      />
      <h1>Politique de Confidentialité</h1>

      <p>
        Dans le cadre du formulaire de contact disponible sur ce site,{" "}
        <strong>ImpactIsol</strong> (SAS, SIREN 953 329 364), située au 500 Route
        du Ruisset, 38360 NOYAREY, France, collecte et traite vos données personnelles
        (nom, prénom, email, téléphone, message) uniquement dans le but de
        répondre à votre demande de contact ou d’information.
      </p>

      <h2>Consentement</h2>
      <p>
        Les données collectées sont fondées sur votre consentement explicite,
        notamment via une case à cocher obligatoire sur le formulaire.
      </p>

      <h2>Destinataires des données</h2>
      <p>
        Seules les personnes habilitées au sein de l’entreprise sont
        destinataires de ces données. Vos données ne sont ni cédées, ni
        commercialisées.
      </p>
      <p>
        Pour faire fonctionner le site, nous faisons appel à des prestataires
        techniques (sous-traitants au sens du RGPD), qui n’utilisent pas vos
        données pour leur propre compte :
      </p>
      <ul>
        <li>
          <strong>Amazon Web Services</strong> (AWS EMEA SARL, Luxembourg) :
          hébergement du site et acheminement des messages du formulaire de
          contact (traités sur des serveurs situés en France, région Paris) ;
        </li>
        <li>
          <strong>Google</strong> (Google Ireland Ltd) : mesure d’audience,
          uniquement si vous l’avez acceptée (voir la section « Cookies »
          ci-dessous). Certaines données peuvent être transférées aux
          États-Unis, dans le cadre du Data Privacy Framework UE–États-Unis
          reconnu par la Commission européenne.
        </li>
      </ul>

      <h2>Durée de conservation</h2>
      <p>
        Vos données sont conservées pendant une durée maximale de 3 ans à
        compter de notre dernier contact, puis elles sont supprimées.
      </p>

      <h2>Vos droits</h2>
      <p>
        Conformément à la réglementation en vigueur (RGPD et loi Informatique &
        Libertés), vous disposez des droits suivants concernant vos données
        personnelles :
      </p>
      <ul>
        <li>Droit d’accès</li>
        <li>Droit de rectification</li>
        <li>Droit à l’effacement</li>
        <li>Droit à la limitation du traitement</li>
        <li>Droit d’opposition</li>
        <li>Droit à la portabilité</li>
      </ul>
      <p>
        Pour exercer ces droits, vous pouvez nous contacter via le formulaire de
        contact ou par mail à{" "}
        <a href="mailto:contact@impactisol.fr">contact@impactisol.fr</a>.
      </p>

      <h2>Droit de réclamation</h2>
      <p>
        Vous pouvez introduire une réclamation auprès de la CNIL (Commission
        Nationale de l’Informatique et des Libertés) si vous estimez que le
        traitement de vos données n’est pas conforme.
      </p>

      <h2>Cookies et mesure d’audience</h2>
      <p>
        Uniquement si vous l’acceptez via le bandeau affiché lors de votre
        première visite, ce site utilise <strong>Google Analytics</strong> pour
        mesurer son audience : pages consultées, provenance des visites (moteur
        de recherche, réseaux sociaux…), type d’appareil, ville approximative,
        clics sur les liens de contact. Ces statistiques sont anonymes et servent
        uniquement à améliorer le site. Les données sont traitées par Google
        (Google Ireland Ltd) et conservées 14 mois maximum.
      </p>
      <p>
        Si vous refusez, aucun cookie de mesure n’est déposé et le site
        fonctionne exactement de la même façon.
      </p>
      <p>
        Vous pouvez changer d’avis à tout moment :{" "}
        <ConsentButton onClick={resetConsent}>
          modifier mon choix concernant les cookies
        </ConsentButton>
      </p>

      <h2>Sécurité</h2>
      <p>
        Nous mettons en œuvre des mesures techniques et organisationnelles
        appropriées pour assurer la sécurité de vos données et prévenir tout
        accès non autorisé.
      </p>

      <p>
        Pour toute question relative à la protection des données personnelles,
        vous pouvez nous contacter à l’adresse mail indiquée.
      </p>

      <p>
        <em>Dernière mise à jour : 27 septembre 2026</em>
      </p>
    </PolitiqueContainer>
  );
};

export default PolitiqueConfidentialite;
